import { Link } from 'react-router-dom';
import type { DirecaoDaOrdem, LeadListRow, OrdenacaoDeLeads } from '../features/leads/types';
import { CANAL, DESFECHO_LABEL, descreverRelativo, SITUACAO } from '../features/leads/vocabulario';
import { formatBRLFromCents, formatDateTime, maskPhoneDisplay, shortenName, PROVIDER_LABEL, VALOR_AUSENTE, type StatusTone } from '../lib/format';
import { Badge, Icon } from './ui';

/**
 * Tabela de leads (RNF15 / RNF16).
 *
 * RESPONSIVIDADE. Em tela estreita nao existe tabela: existe uma lista de
 * cartoes. Empurrar sete colunas para dentro de 360px so produz rolagem
 * horizontal, que e a forma mais confiavel de esconder dado em celular -- o
 * leitor nao descobre que existe uma coluna a direita.
 *
 * As duas formas saem do MESMO array, e quem escolhe qual aparece e o CSS
 * (`hidden md:block` / `md:hidden`), nao um matchMedia em JavaScript. Isso
 * mantem a decisao de layout no mesmo lugar que o resto da casca do painel e
 * evita o primeiro render no formato errado. O custo e ter os dois ramos no
 * DOM; e um custo limitado porque a lista e carregada em fatias ("ver mais"),
 * e `display:none` tira o ramo oculto tambem da arvore de acessibilidade.
 *
 * ORDENACAO. Tres cabecalhos sao botoes (valor, situacao, saiu em) e mandam a
 * coluna para a pagina, que escreve a consulta na URL e refaz a busca: a
 * ordem e do servidor, nao um truque local sobre 25 linhas. `aria-sort` no
 * `<th>` diz ao leitor de tela qual coluna manda.
 *
 * PRIVACIDADE. Nome sai abreviado e telefone sai mascarado. Um painel destes
 * fica aberto o dia inteiro numa tela de escritorio, e quem ligou foi a IA --
 * o operador nao precisa do numero completo para acompanhar a fila.
 *
 * NAVEGACAO. Cada linha leva ao detalhe (`/leads/:id`), preservando a busca
 * da URL (periodo e filtros) como os outros links do painel. Na tabela,
 * `<tr>` nao pode ser link: o link fica na celula do nome e um pseudo-elemento
 * esticado (`after:absolute after:inset-0`, com `relative` na linha) cobre a
 * linha inteira. O nome acessivel do link e completo ("Abrir lead ...") porque
 * o texto visivel de um lead sem nome seria so um travessao. No celular, o
 * cartao inteiro e o link.
 */

interface LeadsTableProps {
  readonly leads: readonly LeadListRow[];
  /** "Agora" da tela: AGORA_SIMULADO na previa, relogio real em producao. */
  readonly agora: Date;
  readonly sort: OrdenacaoDeLeads;
  readonly dir: DirecaoDaOrdem;
  readonly onSort: (coluna: OrdenacaoDeLeads) => void;
}

const PONTO_POR_TOM: Readonly<Record<StatusTone, string>> = {
  good: 'bg-status-good',
  warning: 'bg-status-warning',
  serious: 'bg-status-serious',
  critical: 'bg-status-critical',
  neutral: 'bg-ink-subtle',
};

/**
 * Selo de estado: ponto colorido + rotulo escrito, sempre juntos.
 *
 * A cor de status e reforco, nunca o canal principal. Tres dos quatro tons
 * ficam abaixo de 3:1 no fundo claro; sem o texto ao lado, o estado
 * desapareceria para uma parte dos leitores.
 */
export function StatusPill({
  tone,
  label,
}: {
  readonly tone: StatusTone;
  readonly label: string;
}): JSX.Element {
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-sm text-ink">
      <span aria-hidden="true" className={`h-2 w-2 shrink-0 rounded-full ${PONTO_POR_TOM[tone]}`} />
      {label}
    </span>
  );
}

function destinoDoLead(lead: LeadListRow): { readonly pathname: string; readonly search: string } {
  return { pathname: `/leads/${encodeURIComponent(lead.id)}`, search: window.location.search };
}

function nomeAcessivel(lead: LeadListRow): string {
  return `Abrir lead ${shortenName(lead.name)}, ${maskPhoneDisplay(lead.phoneE164)}`;
}

/**
 * Resume o estagio da regua numa frase curta, a partir da situacao: o que ja
 * foi feito (contatos) e o que vem (proximo toque), sem repetir o selo.
 */
function descreverProgresso(lead: LeadListRow, agora: Date): string {
  const contatos = lead.contacts === 1 ? '1 contato' : `${lead.contacts} contatos`;
  switch (lead.situacao) {
    case 'na_fila':
      return lead.nextTouch === null ? 'esperando o 1º toque' : `1º toque ${descreverRelativo(lead.nextTouch.scheduledAt, agora)}`;
    case 'em_recuperacao':
      return lead.nextTouch === null ? `${contatos} · aguardando o fim da régua` : `${contatos} · próximo ${descreverRelativo(lead.nextTouch.scheduledAt, agora)}`;
    case 'com_comercial':
      return `${contatos} · respondeu`;
    case 'recuperado':
      return lead.recoveredAt === null ? contatos : `pagou ${descreverRelativo(lead.recoveredAt, agora)}`;
    case 'pagou_sozinho':
      return lead.recoveredAt === null ? 'sem toque' : `pagou ${descreverRelativo(lead.recoveredAt, agora)} · sem toque`;
    case 'esgotado':
      return `${contatos} sem resposta`;
    case 'falhou':
      return `${contatos} · motor falhou`;
    case 'cancelado':
      return contatos;
    default: {
      const nunca: never = lead.situacao;
      return nunca;
    }
  }
}

function Situacao({ lead, agora }: { readonly lead: LeadListRow; readonly agora: Date }): JSX.Element {
  const s = SITUACAO[lead.situacao];
  return (
    <span className="flex flex-col" title={s.descricao}>
      <StatusPill tone={s.pill} label={s.label} />
      <span className="mt-0.5 block text-xs text-ink-subtle">{descreverProgresso(lead, agora)}</span>
    </span>
  );
}

function UltimoContato({ lead, agora }: { readonly lead: LeadListRow; readonly agora: Date }): JSX.Element {
  if (lead.lastContact === null) {
    return (
      <span className="flex flex-col">
        <span className="text-ink-subtle">{VALOR_AUSENTE}</span>
        <span className="text-xs text-ink-subtle">ainda sem contato</span>
      </span>
    );
  }
  const c = CANAL[lead.lastContact.channel];
  return (
    <span className="flex flex-col">
      <span className="inline-flex items-center gap-1.5 text-ink">
        <Icon name={c.icon} size="xs" className="text-ink-muted" />
        <span className="tabular-nums">{descreverRelativo(lead.lastContact.at, agora)}</span>
      </span>
      <span className="text-xs text-ink-subtle">
        {c.curto}
        {lead.lastContact.touchLabel === '' ? '' : ` ${lead.lastContact.touchLabel}`} · {DESFECHO_LABEL[lead.lastContact.outcome]}
      </span>
    </span>
  );
}

function ProximoToque({ lead, agora }: { readonly lead: LeadListRow; readonly agora: Date }): JSX.Element {
  if (lead.nextTouch === null) {
    return <span className="text-ink-subtle">{VALOR_AUSENTE}</span>;
  }
  const c = CANAL[lead.nextTouch.channel];
  return (
    <span className="flex flex-col">
      <span className="tabular-nums text-ink">{descreverRelativo(lead.nextTouch.scheduledAt, agora)}</span>
      <span className="inline-flex items-center gap-1 text-xs text-ink-subtle">
        <Icon name={c.icon} size="xs" />
        {c.curto} {lead.nextTouch.label}
      </span>
    </span>
  );
}

function Valor({ lead }: { readonly lead: LeadListRow }): JSX.Element {
  return (
    <span className="flex flex-col items-end gap-1">
      <span className="tabular-nums text-ink">{formatBRLFromCents(lead.amountCents)}</span>
      {lead.highValue ? (
        <Badge tone="accent" icon="estrela">
          Alto valor
        </Badge>
      ) : null}
    </span>
  );
}

function SaiuEm({ lead }: { readonly lead: LeadListRow }): JSX.Element {
  return (
    <span className="flex flex-col">
      <time dateTime={lead.abandonedAt} className="tabular-nums text-ink-muted">
        {formatDateTime(lead.abandonedAt)}
      </time>
      <span className="text-xs text-ink-subtle">{lead.funnel === 'expirados' ? 'plano venceu' : 'abandonou o checkout'}</span>
    </span>
  );
}

function CabecalhoOrdenavel({
  coluna,
  label,
  sort,
  dir,
  onSort,
  numeric = false,
}: {
  readonly coluna: OrdenacaoDeLeads;
  readonly label: string;
  readonly sort: OrdenacaoDeLeads;
  readonly dir: DirecaoDaOrdem;
  readonly onSort: (coluna: OrdenacaoDeLeads) => void;
  readonly numeric?: boolean;
}): JSX.Element {
  const ativa = sort === coluna;
  return (
    <th scope="col" aria-sort={ativa ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'} data-numeric={numeric ? 'true' : undefined} className={`px-4 py-2 font-medium ${numeric ? 'text-right' : ''}`}>
      <button
        type="button"
        onClick={() => onSort(coluna)}
        className={`inline-flex h-8 items-center gap-1 rounded-md px-1 uppercase tracking-wide transition-colors hover:text-ink ${ativa ? 'text-ink' : ''}`}
      >
        {label}
        <span aria-hidden="true" className={ativa ? 'text-brand' : 'text-ink-subtle/70'}>
          {ativa ? (dir === 'asc' ? '▲' : '▼') : '↕'}
        </span>
        <span className="sr-only">{ativa ? (dir === 'asc' ? ', em ordem crescente; ativar inverte' : ', em ordem decrescente; ativar inverte') : ', ativar ordena por esta coluna'}</span>
      </button>
    </th>
  );
}

export function LeadsTable({ leads, agora, sort, dir, onSort }: LeadsTableProps): JSX.Element {
  return (
    <>
      {/* Desktop */}
      <div className="hidden overflow-x-auto rounded-xl border border-line bg-surface md:block">
        <table className="w-full min-w-[64rem] border-collapse text-sm">
          <caption className="sr-only">
            Leads que abandonaram o checkout ou tiveram o plano vencido, com situação na régua, último contato, próximo toque e valor. Cada linha abre o detalhe do lead.
          </caption>
          <thead>
            <tr className="border-b border-line text-left text-xs uppercase tracking-wide text-ink-subtle">
              <th scope="col" className="px-4 py-3 font-medium">Lead</th>
              <th scope="col" className="px-4 py-3 font-medium">Oferta</th>
              <CabecalhoOrdenavel coluna="amount" label="Valor" sort={sort} dir={dir} onSort={onSort} numeric />
              <CabecalhoOrdenavel coluna="situacao" label="Situação" sort={sort} dir={dir} onSort={onSort} />
              <th scope="col" className="px-4 py-3 font-medium">Último contato</th>
              <th scope="col" className="px-4 py-3 font-medium">Próximo toque</th>
              <CabecalhoOrdenavel coluna="abandonedAt" label="Saiu em" sort={sort} dir={dir} onSort={onSort} />
              <th scope="col" className="px-2 py-3 font-medium">
                <span className="sr-only">Abrir</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {leads.map((lead) => (
              <tr key={lead.id} className="group relative border-b border-line/60 transition-colors last:border-0 hover:bg-ink/5 focus-within:bg-ink/5">
                <th scope="row" className="px-4 py-3 text-left font-normal">
                  <Link to={destinoDoLead(lead)} aria-label={nomeAcessivel(lead)} className="block rounded-md after:absolute after:inset-0 after:content-[''] group-hover:text-brand">
                    <span className="block text-ink group-hover:text-brand">{shortenName(lead.name)}</span>
                    <span className="block text-xs text-ink-subtle">{maskPhoneDisplay(lead.phoneE164)}</span>
                  </Link>
                </th>
                <td className="px-4 py-3 text-ink-muted">
                  <span className="block max-w-[16rem] truncate" title={lead.productName ?? undefined}>
                    {lead.productName ?? VALOR_AUSENTE}
                  </span>
                  <span className="block text-xs text-ink-subtle">{PROVIDER_LABEL[lead.provider]}</span>
                </td>
                <td data-numeric="true" className="px-4 py-3 text-right">
                  <Valor lead={lead} />
                </td>
                <td className="px-4 py-3">
                  <Situacao lead={lead} agora={agora} />
                </td>
                <td className="px-4 py-3">
                  <UltimoContato lead={lead} agora={agora} />
                </td>
                <td className="px-4 py-3">
                  <ProximoToque lead={lead} agora={agora} />
                </td>
                <td className="px-4 py-3">
                  <SaiuEm lead={lead} />
                </td>
                <td className="px-2 py-3 text-ink-subtle group-hover:text-brand" aria-hidden="true">
                  <Icon name="seta-dir" size="sm" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile */}
      <ul className="flex flex-col gap-3 md:hidden">
        {leads.map((lead) => (
          <li key={lead.id}>
            <Link
              to={destinoDoLead(lead)}
              aria-label={nomeAcessivel(lead)}
              className="flex flex-col gap-3 rounded-xl border border-line bg-surface px-4 py-4 transition-colors hover:border-brand/60 active:bg-ink/5"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate font-medium text-ink">{shortenName(lead.name)}</p>
                  <p className="truncate text-xs text-ink-subtle">{maskPhoneDisplay(lead.phoneE164)}</p>
                </div>
                <div className="shrink-0 text-right">
                  <Valor lead={lead} />
                </div>
              </div>

              <p className="text-sm text-ink-muted">
                {lead.productName ?? VALOR_AUSENTE}
                <span className="text-ink-subtle"> · {PROVIDER_LABEL[lead.provider]}</span>
              </p>

              <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2 border-t border-line pt-3">
                <Situacao lead={lead} agora={agora} />
                <SaiuEm lead={lead} />
              </div>

              <dl className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <dt className="text-xs text-ink-subtle">Último contato</dt>
                  <dd>
                    <UltimoContato lead={lead} agora={agora} />
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-ink-subtle">Próximo toque</dt>
                  <dd>
                    <ProximoToque lead={lead} agora={agora} />
                  </dd>
                </div>
              </dl>
            </Link>
          </li>
        ))}
      </ul>
    </>
  );
}
